import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { STATS } from "@/lib/site";
import FinalCTA from "@/components/site/FinalCTA";
import SEO, { breadcrumbsLd } from "@/components/site/SEO";

const HERO =
  "https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZ2xhc3MlMjBtb2Rlcm58ZW58MHx8fHwxNzgxMTkxMDgwfDA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { title: "Quietly professional", body: "We dress the building, our team and our communication for a working day, not a brochure." },
  { title: "Fair, not flashy", body: "Transparent pricing, plain-English leases, and no nickel-and-diming once you have moved in." },
  { title: "Built to grow with you", body: "Most of our tenants change suite at least once. We make that change painless." },
];

const AboutPage = () => (
  <>
    <SEO
      title="About Weston Commercial Center"
      description="A serviced office building in Weston, Florida — opened in 2014, home to 120+ small businesses, with 94% tenant retention. Quietly professional, fair, and built to grow with you."
      path="/about"
      jsonLd={[
        breadcrumbsLd([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]),
      ]}
    />
    <section className="bg-[var(--bg)] border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow">About</div>
            <h1 className="hero-display text-[52px] md:text-[96px] mt-5 leading-[1.0]">
              A building for <em className="italic text-[var(--primary)]">working,</em> not staging.
            </h1>
            <p className="mt-7 max-w-xl text-[16px] leading-relaxed text-[var(--text-muted)]">
              Weston Commercial Center opened in 2014 as a serviced office
              building for the small firms shaping west Broward — and we&apos;ve
              been quietly perfecting the formula ever since.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="hairline aspect-[4/3] overflow-hidden">
              <img src={HERO} alt="Building exterior" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow">Our values</div>
            <h2 className="section-title text-[36px] md:text-[52px] mt-4 leading-tight">
              How we think about <em className="italic text-[var(--primary)]">workspace.</em>
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="hairline divide-y divide-[var(--border)]">
              {VALUES.map((v) => (
                <div key={v.title} className="p-8">
                  <h3 className="font-serif text-[26px] leading-tight">{v.title}</h3>
                  <p className="text-[15px] mt-3 text-[var(--text-muted)] leading-relaxed max-w-2xl">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-[var(--bg)] border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20">
        <div className="eyebrow">By the numbers</div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] hairline">
          {STATS.map((s) => (
            <div key={s.label} className="bg-white p-10 text-center">
              <div className="font-serif text-[52px] text-[var(--primary)] leading-none">{s.value}</div>
              <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/contact" className="link-underline">
            Tour the building <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>

    <FinalCTA />
  </>
);

export default AboutPage;
